import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import * as z from "zod";

import actions from "@/actions";
import { RootState } from "@/reducers/types";
import { Button } from "@/sx-layout/common/Button";
import { FormError, FormLabel, FormRow, InputText, Select } from "@/sx-layout/common/Form";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/sx-layout/common/Modal";
import { ColorPickerFormController } from "@/sx-layout/components/plotmap/components/freeDraw/components/ColorPickerFormController";
import { FreeDrawArrow } from "@/sx-layout/components/plotmap/components/freeDraw/models";
import { UserRole } from "@/sx-layout/components/plotmap/models";
import { formatUseHour, formatUseHourString } from "@/sx-layout/components/plotmap/util/formatUseHour";
import { HHMMRegex, normalizeHexColor } from "@/sx-layout/utils";

type Props = {
  freeDrawArrow?: FreeDrawArrow;
  companyId: number;
  color: string;
  thickness: number;
  useStartHour?: string;
  useEndHour?: string;
  onSaveDrawingArrow: (_: {
    company_id: number;
    color: string;
    thickness: number;
    use_start_hour?: string;
    use_end_hour?: string;
  }) => void;
  onSave: (arrow: FreeDrawArrow) => void;
  onClose: () => void;
};

const ARROW_THICKNESS_OPTIONS = [
  { label: "10pt", value: 10 },
  { label: "7pt", value: 7 },
  { label: "5pt", value: 5 },
  { label: "3pt", value: 3 },
  { label: "1pt", value: 1 },
];

export const EditFreeDrawArrowModal: FC<Props> = ({
  freeDrawArrow,
  companyId,
  color,
  thickness,
  useStartHour,
  useEndHour,
  onSaveDrawingArrow,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();

  const userRole = useSelector((state: RootState) => state.layoutApp.layoutMasters.user_role);
  const isAdmin = userRole === UserRole.MASTER_ROLE;
  const companies = useSelector((state: RootState) => state.layoutApp.layoutMasters.companies);

  const formSchema = z
    .object({
      company_id: z.union([z.number(), z.null()]).refine((value) => !isAdmin || value !== null, {
        message: t("select_company"),
      }),
      color: z.string(),
      thickness: z.number(),
      use_start_hour: z
        .string()
        .optional()
        .refine((hour) => !hour || HHMMRegex.test(hour), {
          message: `${t("operation_time")}：${t("error_start_hour")}`,
        }),
      use_end_hour: z
        .string()
        .optional()
        .refine((hour) => !hour || HHMMRegex.test(hour), {
          message: `${t("operation_time")}：${t("error_end_hour")}`,
        }),
    })
    .refine(
      (data) =>
        !(data.use_start_hour && data.use_end_hour) ||
        formatUseHour(data.use_start_hour) < formatUseHour(data.use_end_hour),
      {
        message: t("work_time_alert"),
        path: ["use_end_hour"],
      }
    )
    .refine((data) => !data.use_end_hour || data.use_start_hour, {
      message: `${t("operation_time")}：${t("input_start_hour_validation")}`,
      path: ["use_start_hour"],
    })
    .refine((data) => !data.use_start_hour || data.use_end_hour, {
      message: `${t("operation_time")}：${t("input_end_hour_validation")}`,
      path: ["use_end_hour"],
    });
  type FormData = z.infer<typeof formSchema>;
  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    reValidateMode: "onSubmit",
    defaultValues: {
      company_id: companyId,
      color: color,
      thickness: thickness,
      use_start_hour: useStartHour || "",
      use_end_hour: useEndHour || "",
    },
  });
  // 会社の選択肢に連動してデフォルトの矢印の色を変える
  const onChangeCompany = (companyId: number) => {
    const companyColor = companies.find((v) => v.company_id === companyId)?.icon_color;
    if (companyColor) {
      setValue("color", normalizeHexColor(companyColor));
    }
  };
  // 時間文字列のフォーマット
  const useStartHourEdited = watch("use_start_hour");
  const onBlurUseStartHour = useCallback(() => {
    setValue("use_start_hour", formatUseHourString(useStartHourEdited));
  }, [useStartHourEdited]);
  const useEndHourEdited = watch("use_end_hour");
  const onBlurUseEndHour = useCallback(() => {
    setValue("use_end_hour", formatUseHourString(useEndHourEdited));
  }, [useEndHourEdited]);

  const dispatch = useDispatch();
  const handleSave = useCallback(
    (values: FormData) => {
      if (freeDrawArrow) {
        dispatch(
          actions.arrows.updateArrow({
            input: {
              arrow_id: freeDrawArrow.arrow_id,
              company_id: freeDrawArrow.company_id, // 会社は更新できない
              color: values.color,
              thickness: values.thickness,
              use_start_hour: values.use_start_hour,
              use_end_hour: values.use_end_hour,
              coords: freeDrawArrow.coords,
              timestamp: {
                update_date: freeDrawArrow.timestamp?.update_date,
              },
            },
            onSuccess: (arrow: FreeDrawArrow) => onSave(arrow),
          })
        );
      } else {
        onSaveDrawingArrow({
          company_id: values.company_id,
          color: values.color,
          thickness: values.thickness,
          use_start_hour: values.use_start_hour,
          use_end_hour: values.use_end_hour,
        });
      }
    },
    [dispatch, freeDrawArrow, onSave]
  );

  return (
    <Modal
      id="arrow-edit-modal"
      onClose={onClose}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      style={{ content: { overflow: "visible" } }}
    >
      <ModalHeader>{t("arrow_edit")}</ModalHeader>
      <ModalBody size="small" className="modal-body items-start" style={{ overflow: "visible" }}>
        <form>
          {/* 会社 */}
          {isAdmin && (
            <FormRow className="items-start">
              <FormLabel className="mt-2">{t("company_title")}</FormLabel>
              {freeDrawArrow ? (
                <span className="mt-2">{freeDrawArrow.company_name}</span>
              ) : (
                <Select
                  className="w-200"
                  // @ts-ignore
                  control={control}
                  name="company_id"
                  handleChange={onChangeCompany}
                  options={companies.map((c) => ({
                    label: c.company_name,
                    value: c.company_id,
                  }))}
                />
              )}
            </FormRow>
          )}
          {/* 色 */}
          <FormRow className="items-start">
            <FormLabel className="mt-2">{t("arrow_color")}</FormLabel>
            <ColorPickerFormController name="color" control={control} />
          </FormRow>
          {/* 太さ*/}
          <FormRow>
            <FormLabel>{t("thickness")}</FormLabel>
            <Select name="thickness" control={control} options={ARROW_THICKNESS_OPTIONS} className="w-100" />
          </FormRow>
          {/* 使用時間 */}
          <FormRow
            errorComponent={
              <>
                <FormError>{errors.use_start_hour?.message}</FormError>
                <FormError>{errors.use_end_hour?.message}</FormError>
              </>
            }
          >
            <FormLabel>{t("operation_time")}</FormLabel>
            <InputText className="w-[80px]" {...register("use_start_hour")} onBlur={onBlurUseStartHour} />
            <span className="mx-3">〜</span>
            <InputText
              className="w-[80px]"
              {...register("use_end_hour", { deps: ["use_start_hour"] })}
              onBlur={onBlurUseEndHour}
            />
          </FormRow>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button buttonType="cancel" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button onClick={handleSubmit(handleSave)}>{t("save")}</Button>
      </ModalFooter>
    </Modal>
  );
};
